import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PlatformCreds {
  platform: string;
  appClientId: string;
  appClientSecretMasked: string;
  redirectBaseUrl: string;
  environment: string;
  notes?: string;
  updatedAt?: string;
  updatedBy?: string;
  hasCredentials: boolean;
  fromAppSettings?: boolean;
}

interface PlatformForm {
  appClientId: string;
  appClientSecret: string;
  redirectBaseUrl: string;
  environment: string;
  notes: string;
}

// ─── Platform definitions ──────────────────────────────────────────────────
//
// credentialsType:
//   "velaro-app"  — single Velaro-registered OAuth app shared across all customers
//                   (BigCommerce, Shopify, Webflow, Gmail, Outlook)
//   "per-site"    — each customer authenticates their own account independently
//                   (HubSpot, Dynamics). No Velaro-level credential to manage here.
//
// Rule: whenever a new OAuth integration is added to velaro-messaging, add it here.

const PLATFORMS = [
  // ── Marketplace installs ──────────────────────────────────────────────────
  {
    key: "BigCommerce",
    label: "BigCommerce",
    credentialsType: "velaro-app" as const,
    logo: "/img/bigcommerce.svg",
    partnerUrl: "https://devtools.bigcommerce.com",
    partnerLabel: "BigCommerce DevTools",
    installPath: "/apps/bigcommerce/install",
    callbackPath: "/BigCommerce/callback",
    gdprPaths: [
      "/BigCommerce/gdpr/store/uninstall",
      "/BigCommerce/gdpr/store/redact",
      "/BigCommerce/gdpr/customer/redact"
    ],
    scopes: "store_v2_customers store_v2_orders store_catalog_products store_content store_v2_information_read_only",
    notes: [
      "Apply at partners.bigcommerce.com → Technology Partner Program",
      "Create app at devtools.bigcommerce.com → get Client ID + Client Secret",
      "Register callback URL in app settings (see URL fields below)",
      "Register GDPR webhook URLs in app settings",
      "Submit for marketplace review once credentials are working on staging"
    ]
  },
  {
    key: "Shopify",
    label: "Shopify",
    credentialsType: "velaro-app" as const,
    logo: "/img/shopify.svg",
    partnerUrl: "https://partners.shopify.com",
    partnerLabel: "Shopify Partners",
    installPath: "/apps/shopify/install",
    callbackPath: "/Shopify/callback",
    gdprPaths: [
      "/Shopify/gdpr/customers/data_request",
      "/Shopify/gdpr/customers/redact",
      "/Shopify/gdpr/shop/redact"
    ],
    scopes: "read_customers read_orders read_products write_customers read_checkouts write_script_tags",
    notes: [
      "Credentials already set via Shopify__ Azure App Settings",
      "This page can override App Settings — leave blank to keep using App Settings",
      "Shopify app is already registered and live on the Partner Dashboard"
    ]
  },
  {
    key: "Webflow",
    label: "Webflow",
    credentialsType: "velaro-app" as const,
    logo: "/img/webflow.svg",
    partnerUrl: "https://webflow.com/dashboard/apps/developer",
    partnerLabel: "Webflow Developer Dashboard",
    installPath: "/apps/webflow/install",
    callbackPath: "/webflow/callback",
    gdprPaths: [],
    scopes: "sites:read custom_code:write",
    notes: [
      "Register the app at webflow.com/dashboard/apps/developer",
      "Set Load URL and OAuth Callback URL (see below)",
      "App Settings: Webflow__ClientId, Webflow__ClientSecret, Webflow__RedirectBaseUrl"
    ]
  },
  // ── Email channels ────────────────────────────────────────────────────────
  {
    key: "Gmail",
    label: "Gmail / Google Workspace",
    credentialsType: "velaro-app" as const,
    logo: "/img/google.svg",
    partnerUrl: "https://console.cloud.google.com",
    partnerLabel: "Google Cloud Console",
    installPath: "/GmailOAuth/auth-url",
    callbackPath: "/GmailOAuth/callback",
    gdprPaths: [],
    scopes: "https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.modify",
    notes: [
      "Create an OAuth 2.0 client at console.cloud.google.com → APIs & Services → Credentials",
      "Add /GmailOAuth/callback as an Authorized Redirect URI",
      "App Settings: Gmail:ClientId, Gmail:ClientSecret",
      "For Domain-Wide Delegation: set Gmail:DwdServiceAccountJson (JSON key file contents)"
    ]
  },
  {
    key: "Outlook",
    label: "Outlook / Microsoft 365",
    credentialsType: "velaro-app" as const,
    logo: "/img/microsoft.svg",
    partnerUrl: "https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps",
    partnerLabel: "Azure App Registrations",
    installPath: "/OutlookOAuth/auth-url",
    callbackPath: "/OutlookOAuth/callback",
    gdprPaths: [],
    scopes: "Mail.Read Mail.Send Mail.ReadWrite offline_access openid profile",
    notes: [
      "Register an app in Azure Active Directory → App registrations",
      "Add /OutlookOAuth/callback as a Redirect URI (Web platform)",
      "Grant API permissions: Microsoft Graph → Mail.Read, Mail.Send, Mail.ReadWrite",
      "App Settings: Graph:ClientId, Graph:ClientSecret"
    ]
  },
  // ── Per-site CRM integrations (no Velaro-level app credential) ────────────
  {
    key: "HubSpot",
    label: "HubSpot",
    credentialsType: "per-site" as const,
    logo: "/img/hubspot.svg",
    partnerUrl: "https://app.hubspot.com/private-apps",
    partnerLabel: "HubSpot Private Apps",
    installPath: "",
    callbackPath: "",
    gdprPaths: [],
    scopes: "crm.objects.contacts.read crm.objects.deals.read crm.objects.contacts.write",
    notes: [
      "HubSpot uses per-site Private App tokens — each customer creates their own",
      "No Velaro-level OAuth app registration needed",
      "Customers connect via Admin → Integrations → HubSpot (enter their Private App token)",
      "Token format: pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
    ]
  },
  {
    key: "Dynamics",
    label: "Microsoft Dynamics 365",
    credentialsType: "per-site" as const,
    logo: "/img/dynamics.svg",
    partnerUrl: "https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps",
    partnerLabel: "Azure App Registrations",
    installPath: "",
    callbackPath: "",
    gdprPaths: [],
    scopes: "Dynamics CRM user_impersonation offline_access",
    notes: [
      "Dynamics uses per-customer Azure AD app registrations — each customer sets up their own",
      "No shared Velaro-level credential — customers provide their own ClientId + ClientSecret",
      "Customers connect via Admin → Integrations → Dynamics (enter their Azure app credentials)",
      "Required: Azure App Registration with Dynamics CRM API permission"
    ]
  }
];

// ─── Credential form for one platform ────────────────────────────────────────

type PlatformDef = typeof PLATFORMS[number];

function PlatformCard({
  platform,
  creds,
  messagingApiBase,
  onSaved
}: {
  platform: PlatformDef;
  creds: PlatformCreds | null;
  messagingApiBase: string;
  onSaved: () => void;
}) {
  const api = useApi();
  const [form, setForm] = useState<PlatformForm>({
    appClientId: creds?.appClientId ?? "",
    appClientSecret: "",
    redirectBaseUrl: creds?.redirectBaseUrl ?? messagingApiBase,
    environment: creds?.environment ?? "",
    notes: creds?.notes ?? ""
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [open, setOpen] = useState(false);

  const isPerSite = platform.credentialsType === "per-site";

  // Expand velaro-app platforms that are missing credentials
  useEffect(() => {
    if (!isPerSite && !creds?.hasCredentials) setOpen(true);
  }, [creds, isPerSite]);

  function set(field: keyof PlatformForm, value: string) {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaved(false);
    setError(null);
  }

  async function save() {
    if (!form.appClientId.trim()) { setError("Client ID is required."); return; }
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, string> = {
        appClientId: form.appClientId.trim(),
        redirectBaseUrl: form.redirectBaseUrl.trim(),
        environment: form.environment.trim(),
        notes: form.notes.trim()
      };
      // Only send secret if user typed a new one
      if (form.appClientSecret.trim()) {
        payload.appClientSecret = form.appClientSecret.trim();
      }
      const resp = await api.admin.put(`PlatformCredentials/${platform.key}`, payload);
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setError(body.error ?? "Save failed.");
        return;
      }
      setSaved(true);
      setForm(prev => ({ ...prev, appClientSecret: "" }));
      onSaved();
    } catch {
      setError("Unexpected error. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  const callbackUrl = `${form.redirectBaseUrl || messagingApiBase}${platform.callbackPath}`;
  const installUrl  = `${form.redirectBaseUrl || messagingApiBase}${platform.installPath}`;

  return (
    <div className="bg-white rounded-2xl shadow-md mb-4 overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 text-left"
      >
        <div className="flex items-center gap-3">
          <img src={platform.logo} alt={platform.label} className="w-7 h-7 object-contain" onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
          <span className="font-semibold text-slate-800">{platform.label}</span>
          {isPerSite ? (
            <span className="text-xs bg-slate-100 text-slate-600 font-medium px-2 py-0.5 rounded-full">Per-site credentials</span>
          ) : creds?.hasCredentials ? (
            creds.fromAppSettings ? (
              <span className="text-xs bg-blue-100 text-blue-700 font-medium px-2 py-0.5 rounded-full">Configured (App Settings)</span>
            ) : (
              <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">Configured</span>
            )
          ) : (
            <span className="text-xs bg-amber-100 text-amber-700 font-medium px-2 py-0.5 rounded-full">Not configured</span>
          )}
          {creds?.updatedAt && (
            <span className="text-xs text-slate-400">
              Last updated {new Date(creds.updatedAt).toLocaleDateString()} {creds.updatedBy ? `by ${creds.updatedBy}` : ""}
            </span>
          )}
        </div>
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>

      {open && (
        <div className="border-t border-slate-100 px-6 pb-6 pt-4 space-y-5">

          {/* Setup / info notes */}
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
            <div className="font-semibold text-blue-900 text-sm mb-2">
              {isPerSite ? "How this integration works" : "Setup steps (one-time, done by Velaro)"}
            </div>
            <ol className="list-decimal list-inside space-y-1 text-xs text-blue-800">
              {platform.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ol>
            <a
              href={platform.partnerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-2 text-xs text-blue-600 underline"
            >
              Open {platform.partnerLabel} →
            </a>
          </div>

          {/* URLs — only for platforms with actual OAuth callback paths */}
          {(platform.installPath || platform.callbackPath) && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-2">
              <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">
                URLs to register in {platform.partnerLabel}
              </div>
              {platform.installPath && <UrlRow label="Install / Load URL" url={installUrl} />}
              {platform.callbackPath && <UrlRow label="OAuth Callback URL" url={callbackUrl} />}
              {platform.gdprPaths.map(p => (
                <UrlRow key={p} label={`Webhook: ${p.split("/").pop()}`}
                  url={`${form.redirectBaseUrl || messagingApiBase}${p}`} />
              ))}
            </div>
          )}

          {/* Credential form — velaro-app platforms only */}
          {!isPerSite && (
            <div className="space-y-4">
              <div className="text-sm font-semibold text-slate-700">App credentials</div>

              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">Client ID</span>
                <input
                  className="input font-mono text-sm"
                  value={form.appClientId}
                  onChange={e => set("appClientId", e.target.value)}
                  placeholder="From partner/developer portal"
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">
                  Client Secret {creds?.hasCredentials && <span className="text-slate-400 font-normal">(current: {creds.appClientSecretMasked} — leave blank to keep)</span>}
                </span>
                <div className="flex gap-2">
                  <input
                    className="input font-mono text-sm flex-1"
                    type={showSecret ? "text" : "password"}
                    value={form.appClientSecret}
                    onChange={e => set("appClientSecret", e.target.value)}
                    placeholder={creds?.hasCredentials ? "Enter new secret to rotate" : "From partner/developer portal"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="px-3 py-2 border border-slate-200 rounded-lg text-xs text-slate-500 hover:bg-slate-50"
                  >
                    {showSecret ? "Hide" : "Show"}
                  </button>
                </div>
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">Messaging API Base URL (for redirect)</span>
                <input
                  className="input text-sm font-mono"
                  value={form.redirectBaseUrl}
                  onChange={e => set("redirectBaseUrl", e.target.value)}
                  placeholder="https://velaro-messaging-api-staging.azurewebsites.net"
                />
                <span className="text-xs text-slate-400">No trailing slash. This is the velaro-messaging API, not the admin frontend.</span>
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">Environment label</span>
                <input
                  className="input text-sm"
                  value={form.environment}
                  onChange={e => set("environment", e.target.value)}
                  placeholder="Staging or Production"
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">Notes</span>
                <textarea
                  className="input text-sm resize-none"
                  rows={2}
                  value={form.notes}
                  onChange={e => set("notes", e.target.value)}
                  placeholder="App listing URL, review status, partner contact, etc."
                />
              </label>
            </div>
          )}

          {/* OAuth scopes reference */}
          {platform.scopes && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="text-xs font-semibold text-slate-600 mb-1">OAuth scopes</div>
              <code className="text-xs text-slate-700 break-all">{platform.scopes}</code>
            </div>
          )}

          {!isPerSite && (
            <>
              {error && <p className="text-sm text-red-600">{error}</p>}
              {saved && <p className="text-sm text-green-600">Credentials saved. velaro-messaging will use these on next request.</p>}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="px-5 py-2.5 bg-slate-800 text-white rounded-lg text-sm font-medium hover:bg-slate-700 disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save Credentials"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function UrlRow({ label, url }: { label: string; url: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  }
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-500 w-40 flex-shrink-0">{label}</span>
      <code className="text-xs text-slate-700 font-mono flex-1 bg-white border border-slate-200 rounded px-2 py-1 truncate">{url}</code>
      <button type="button" onClick={copy} className="text-xs text-slate-400 hover:text-slate-700 flex-shrink-0">
        {copied ? "✓" : "Copy"}
      </button>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AppMarketplace() {
  const api = useApi();
  const [allCreds, setAllCreds] = useState<PlatformCreds[]>([]);
  const [loading, setLoading] = useState(true);

  // ⚠️  Replace with your actual staging messaging API base URL
  // In production this should come from an env var or site config
  const messagingApiBase = import.meta.env.VITE_MESSAGING_API_URL?.replace(/\/$/, "")
    ?? "https://velaro-messaging-api-staging.azurewebsites.net";

  const load = useCallback(async () => {
    try {
      const resp = await api.admin.get("PlatformCredentials");
      const data = await resp.json();
      setAllCreds(Array.isArray(data) ? data : []);
    } catch {
      setAllCreds([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { load(); }, [load]);

  function credsFor(platform: string): PlatformCreds | null {
    return allCreds.find(c => c.platform === platform) ?? null;
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-800 mb-1">App Marketplace Credentials</h1>
        <p className="text-sm text-slate-500">
          Manage OAuth app credentials for each platform marketplace. These are stored in the database
          and override Azure App Settings — no Azure portal access needed for key rotation.
        </p>
      </div>

      <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 mb-6 text-sm text-amber-800">
        <strong>Velaro staff only.</strong> Client Secrets are masked on read and never returned in full.
        Updating credentials takes effect on the next incoming OAuth request — no restart needed.
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
      ) : (
        PLATFORMS.map(platform => (
          <PlatformCard
            key={platform.key}
            platform={platform}
            creds={credsFor(platform.key)}
            messagingApiBase={messagingApiBase}
            onSaved={load}
          />
        ))
      )}
    </div>
  );
}
